import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserIcon,
  ArrowLeftIcon,
  EnvelopeIcon,
  PhoneIcon,
  CalendarIcon,
  KeyIcon,
  AcademicCapIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  CameraIcon,
  MapPinIcon,
  IdentificationIcon,
} from '@heroicons/react/24/outline';
import studentAuthService from '../../../services/studentAuth';

const StudentProfile = () => {
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditingDOB, setIsEditingDOB] = useState(false);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [editedDOB, setEditedDOB] = useState('');
  const [editedAddress, setEditedAddress] = useState('');
  const [updating, setUpdating] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePictureLoading, setProfilePictureLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);

  useEffect(() => {
    loadStudentInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadStudentInfo = async () => {
    try {
      setLoading(true);
      const data = await studentAuthService.getStudentInfo();
      setStudent(data);

      // Load profile picture if student has one
      if (data?.profile_picture_filename) {
        loadProfilePicture();
      }
    } catch (error) {
      console.error('Failed to load student info:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProfilePicture = async () => {
    try {
      setProfilePictureLoading(true);
      const blob = await studentAuthService.getProfilePicture();
      const url = URL.createObjectURL(blob);
      setProfilePicture(url);
    } catch (error) {
      console.error('Error loading profile picture:', error);
      // Don't show error for 404 (no profile picture)
      if (error.response?.status !== 404) {
        alert('Failed to load profile picture');
      }
    } finally {
      setProfilePictureLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    // Treat the datetime string as UTC by appending 'Z' if not present
    const utcString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    return new Date(utcString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'Asia/Kolkata'
    });
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    // Treat the datetime string as UTC by appending 'Z' if not present
    const utcString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    const date = new Date(utcString);
    // Get the date in IST and format for input
    const istDateStr = date.toLocaleDateString('en-US', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    // Convert MM/DD/YYYY to YYYY-MM-DD
    const [month, day, year] = istDateStr.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  const handleEditDOB = () => {
    setEditedDOB(formatDateForInput(student.date_of_birth));
    setIsEditingDOB(true);
  };

  const handleCancelDOB = () => {
    setIsEditingDOB(false);
    setEditedDOB('');
  };

  const handleSaveDOB = async () => {
    try {
      setUpdating(true);
      await studentAuthService.updateProfile({
        date_of_birth: editedDOB ? new Date(editedDOB).toISOString() : null,
      });
      await loadStudentInfo();
      setIsEditingDOB(false);
    } catch (error) {
      console.error('Failed to update date of birth:', error);
      alert('Failed to update date of birth');
    } finally {
      setUpdating(false);
    }
  };

  const handleEditAddress = () => {
    setEditedAddress(student.address || '');
    setIsEditingAddress(true);
  };

  const handleCancelAddress = () => {
    setIsEditingAddress(false);
    setEditedAddress('');
  };

  const handleSaveAddress = async () => {
    try {
      setUpdating(true);
      await studentAuthService.updateProfile({
        address: editedAddress,
      });
      await loadStudentInfo();
      setIsEditingAddress(false);
    } catch (error) {
      console.error('Failed to update address:', error);
      alert('Failed to update address');
    } finally {
      setUpdating(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }

      setSelectedFile(file);
    }
  };

  const handleUploadProfilePicture = async () => {
    if (!selectedFile) return;

    try {
      setProfilePictureLoading(true);

      // Convert file to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const base64String = e.target.result.split(',')[1]; // Remove data:image/...;base64, prefix

          const uploadData = {
            profile_picture: base64String,
            profile_picture_filename: selectedFile.name,
            profile_picture_content_type: selectedFile.type,
          };

          await studentAuthService.uploadProfilePicture(uploadData);
          alert('Profile picture uploaded successfully');

          // Update student data in localStorage
          const updatedStudent = {
            ...student,
            profile_picture_filename: selectedFile.name,
            profile_picture_content_type: selectedFile.type,
          };
          localStorage.setItem('studentUser', JSON.stringify(updatedStudent));
          setStudent(updatedStudent);

          // Set the profile picture directly from the uploaded file
          const fileUrl = URL.createObjectURL(selectedFile);
          setProfilePicture(fileUrl);
          setSelectedFile(null);
        } catch (error) {
          console.error('Error uploading profile picture:', error);
          alert('Failed to upload profile picture');
        } finally {
          setProfilePictureLoading(false);
        }
      };

      reader.readAsDataURL(selectedFile);
    } catch (error) {
      console.error('Error processing file:', error);
      alert('Failed to process file');
      setProfilePictureLoading(false);
    }
  };

  const handleDeleteProfilePicture = async () => {
    try {
      setProfilePictureLoading(true);
      await studentAuthService.deleteProfilePicture();
      alert('Profile picture deleted successfully');

      // Update student data in localStorage
      const updatedStudent = {
        ...student,
        profile_picture_filename: null,
        profile_picture_content_type: null,
      };
      localStorage.setItem('studentUser', JSON.stringify(updatedStudent));
      setStudent(updatedStudent);

      setProfilePicture(null);
    } catch (error) {
      console.error('Error deleting profile picture:', error);
      alert('Failed to delete profile picture');
    } finally {
      setProfilePictureLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 text-lg">Failed to load profile</p>
          <button
            onClick={() => navigate('/student/dashboard')}
            className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Header */}
      <div className="bg-gray-800/50 backdrop-blur-sm border-b border-gray-700/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3 sm:py-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={() => navigate('/student/dashboard')}
                className="p-1.5 sm:p-2 hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5 sm:h-6 sm:w-6 text-gray-400" />
              </button>
              <div>
                <h1 className="text-lg sm:text-2xl font-bold text-white">My Profile</h1>
                <p className="text-xs sm:text-sm text-gray-400 hidden sm:block">
                  View and manage your information
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/student/change-password')}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all hover:scale-105 text-sm"
            >
              <KeyIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="hidden sm:inline">Change Password</span>
              <span className="sm:hidden">Password</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Profile Header Card */}
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-gray-700/50 overflow-hidden mb-4 sm:mb-6 hover:border-blue-500/50 transition-all">
          {/* Gradient Banner */}
          <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 h-24 sm:h-32"></div>

          {/* Profile Info */}
          <div className="px-4 sm:px-6 lg:px-8 pb-6">
            <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-12 sm:-mt-16">
              {/* Avatar Section */}
              <div className="flex flex-col items-center sm:items-start">
                <div className="relative group">
                  <div
                    className={`w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 p-1 ${profilePicture ? 'cursor-pointer' : ''}`}
                    onClick={() => profilePicture && setShowImageModal(true)}
                  >
                    <div className="w-full h-full rounded-full bg-gray-800 flex items-center justify-center overflow-hidden">
                      {profilePicture ? (
                        <img
                          src={profilePicture}
                          alt="Profile"
                          className="h-full w-full object-cover transition-transform group-hover:scale-110"
                        />
                      ) : (
                        <span className="text-3xl sm:text-4xl font-bold text-white">
                          {getInitials(student.first_name, student.last_name)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Click to Zoom Hint */}
                  {profilePicture && !profilePictureLoading && (
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all pointer-events-none">
                      <span className="text-white text-xs font-medium">Click to zoom</span>
                    </div>
                  )}

                  {/* Loading Overlay */}
                  {profilePictureLoading && (
                    <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                    </div>
                  )}
                </div>

                {/* Profile Picture Controls - Mobile Friendly */}
                <div className="flex items-stretch gap-2 mt-3">
                  <label className="cursor-pointer bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-2 shadow-lg transition-all hover:scale-105 flex items-center justify-center gap-2 h-9">
                    <CameraIcon className="h-4 w-4 flex-shrink-0" />
                    <span className="text-xs font-medium whitespace-nowrap">Upload</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </label>

                  {profilePicture && (
                    <button
                      onClick={handleDeleteProfilePicture}
                      disabled={profilePictureLoading}
                      className="bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-lg px-3 py-2 shadow-lg transition-all hover:scale-105 flex items-center justify-center gap-2 h-9"
                    >
                      <XMarkIcon className="h-4 w-4 flex-shrink-0" />
                      <span className="text-xs font-medium whitespace-nowrap">Remove</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Name and File Upload */}
              <div className="flex-1 text-center sm:text-left">
                <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white">
                  {student.first_name} {student.last_name}
                </h2>
                <div className="flex items-center justify-center sm:justify-start gap-2 mt-1">
                  <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full bg-blue-900/30 text-blue-300 border border-blue-700">
                    <IdentificationIcon className="h-3 w-3" />
                    {student.student_id}
                  </span>
                </div>

                {/* File Selection */}
                {selectedFile && (
                  <div className="mt-3 p-3 bg-gray-700/50 backdrop-blur-sm rounded-lg border border-gray-600/50">
                    <p className="text-white text-xs sm:text-sm mb-2 truncate">
                      📷 Selected: {selectedFile.name}
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={handleUploadProfilePicture}
                        disabled={profilePictureLoading}
                        className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white text-xs sm:text-sm rounded-lg transition-all hover:scale-105 font-medium"
                      >
                        {profilePictureLoading ? 'Uploading...' : 'Confirm Upload'}
                      </button>
                      <button
                        onClick={() => setSelectedFile(null)}
                        disabled={profilePictureLoading}
                        className="px-3 py-2 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 text-white text-xs sm:text-sm rounded-lg transition-colors font-medium"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Info Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* Personal Information */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-4 sm:p-6 hover:border-blue-500/50 transition-all">
            <h3 className="text-base sm:text-lg font-bold text-white mb-4 flex items-center gap-2">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <UserIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400" />
              </div>
              Personal Information
            </h3>
            <div className="space-y-3">
              <div className="bg-gray-700/50 rounded-lg p-3">
                <p className="text-xs text-gray-400 mb-1">First Name</p>
                <p className="text-sm sm:text-base text-white font-medium">{student.first_name}</p>
              </div>
              <div className="bg-gray-700/50 rounded-lg p-3">
                <p className="text-xs text-gray-400 mb-1">Last Name</p>
                <p className="text-sm sm:text-base text-white font-medium">{student.last_name}</p>
              </div>
              <div className="bg-gray-700/50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs text-gray-400">Date of Birth</p>
                  {!isEditingDOB && (
                    <button
                      onClick={handleEditDOB}
                      className="p-1 hover:bg-gray-600 rounded transition-colors"
                      title="Edit Date of Birth"
                    >
                      <PencilIcon className="h-3 w-3 sm:h-4 sm:w-4 text-blue-400" />
                    </button>
                  )}
                </div>
                {isEditingDOB ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={editedDOB}
                      onChange={(e) => setEditedDOB(e.target.value)}
                      className="flex-1 px-2 py-1 bg-gray-600 border border-gray-500 rounded text-white text-sm"
                      disabled={updating}
                    />
                    <button
                      onClick={handleSaveDOB}
                      disabled={updating}
                      className="p-1 bg-green-600 hover:bg-green-700 rounded transition-colors disabled:opacity-50"
                      title="Save"
                    >
                      <CheckIcon className="h-4 w-4 text-white" />
                    </button>
                    <button
                      onClick={handleCancelDOB}
                      disabled={updating}
                      className="p-1 bg-red-600 hover:bg-red-700 rounded transition-colors disabled:opacity-50"
                      title="Cancel"
                    >
                      <XMarkIcon className="h-4 w-4 text-white" />
                    </button>
                  </div>
                ) : (
                  <p className="text-sm sm:text-base text-white font-medium">{formatDate(student.date_of_birth)}</p>
                )}
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-4 sm:p-6 hover:border-green-500/50 transition-all">
            <h3 className="text-base sm:text-lg font-bold text-white mb-4 flex items-center gap-2">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <PhoneIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-400" />
              </div>
              Contact Information
            </h3>
            <div className="space-y-3">
              <div className="bg-gray-700/50 rounded-lg p-3">
                <p className="text-xs text-gray-400 mb-1">Email</p>
                <div className="flex items-center gap-2">
                  <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                  <p className="text-sm sm:text-base text-white font-medium break-all">{student.email}</p>
                </div>
              </div>
              <div className="bg-gray-700/50 rounded-lg p-3">
                <p className="text-xs text-gray-400 mb-1">Phone Number</p>
                <div className="flex items-center gap-2">
                  <PhoneIcon className="h-4 w-4 text-gray-400" />
                  <p className="text-sm sm:text-base text-white font-medium">{student.phone_number}</p>
                </div>
              </div>
              <div className="bg-gray-700/50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs text-gray-400">Address</p>
                  {!isEditingAddress && (
                    <button
                      onClick={handleEditAddress}
                      className="p-1 hover:bg-gray-600 rounded transition-colors"
                      title="Edit Address"
                    >
                      <PencilIcon className="h-3 w-3 sm:h-4 sm:w-4 text-blue-400" />
                    </button>
                  )}
                </div>
                {isEditingAddress ? (
                  <div className="space-y-2">
                    <textarea
                      value={editedAddress}
                      onChange={(e) => setEditedAddress(e.target.value)}
                      className="w-full px-2 py-1 bg-gray-600 border border-gray-500 rounded text-white text-sm"
                      rows={3}
                      disabled={updating}
                      placeholder="Enter address"
                    />
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleSaveAddress}
                        disabled={updating}
                        className="px-3 py-1 bg-green-600 hover:bg-green-700 rounded transition-colors disabled:opacity-50 text-white text-sm font-medium"
                      >
                        {updating ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={handleCancelAddress}
                        disabled={updating}
                        className="px-3 py-1 bg-red-600 hover:bg-red-700 rounded transition-colors disabled:opacity-50 text-white text-sm font-medium"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-2">
                    <MapPinIcon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <p className="text-sm sm:text-base text-white font-medium">
                      {student.address || 'No address provided'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Academic Information */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-4 sm:p-6 hover:border-yellow-500/50 transition-all lg:col-span-2">
            <h3 className="text-base sm:text-lg font-bold text-white mb-4 flex items-center gap-2">
              <div className="p-2 bg-yellow-500/20 rounded-lg">
                <AcademicCapIcon className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400" />
              </div>
              Academic Information
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-gray-700/50 rounded-lg p-3">
                <p className="text-xs text-gray-400 mb-1">Enrollment Date</p>
                <div className="flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4 text-gray-400" />
                  <p className="text-sm sm:text-base text-white font-medium">
                    {formatDate(student.enrollment_date)}
                  </p>
                </div>
              </div>
              <div className="bg-gray-700/50 rounded-lg p-3">
                <p className="text-xs text-gray-400 mb-1">Student ID</p>
                <div className="flex items-center gap-2">
                  <IdentificationIcon className="h-4 w-4 text-gray-400" />
                  <p className="text-sm sm:text-base text-white font-medium font-mono">
                    {student.student_id}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-4 sm:mt-6 bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-bold text-white mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <button
              onClick={() => navigate('/student/attendance')}
              className="group p-4 bg-gradient-to-br from-green-600/20 to-green-800/20 hover:from-green-600/30 hover:to-green-800/30 border border-green-600/50 hover:border-green-500 rounded-xl transition-all hover:scale-105 text-left"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/20 rounded-lg group-hover:bg-green-500/30 transition-colors">
                  <CalendarIcon className="h-6 w-6 sm:h-8 sm:w-8 text-green-400" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm sm:text-base">View Attendance</p>
                  <p className="text-xs sm:text-sm text-gray-400">Check your attendance records</p>
                </div>
              </div>
            </button>
            <button
              onClick={() => navigate('/student/tests')}
              className="group p-4 bg-gradient-to-br from-blue-600/20 to-blue-800/20 hover:from-blue-600/30 hover:to-blue-800/30 border border-blue-600/50 hover:border-blue-500 rounded-xl transition-all hover:scale-105 text-left"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg group-hover:bg-blue-500/30 transition-colors">
                  <AcademicCapIcon className="h-6 w-6 sm:h-8 sm:w-8 text-blue-400" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm sm:text-base">View Tests</p>
                  <p className="text-xs sm:text-sm text-gray-400">See your test results</p>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Image Zoom Modal */}
      {showImageModal && profilePicture && (
        <div
          className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowImageModal(false)}
        >
          <div className="relative max-w-4xl w-full">
            {/* Close Button */}
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute -top-12 right-0 p-2 bg-gray-800/50 hover:bg-gray-700 rounded-lg transition-colors"
            >
              <XMarkIcon className="h-6 w-6 text-white" />
            </button>

            {/* Image */}
            <div className="bg-gray-800/50 backdrop-blur-md rounded-2xl overflow-hidden border border-gray-700/50">
              <img
                src={profilePicture}
                alt="Profile - Full Size"
                className="w-full h-auto max-h-[80vh] object-contain"
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            {/* Info */}
            <div className="mt-4 text-center">
              <p className="text-white font-semibold text-lg">
                {student.first_name} {student.last_name}
              </p>
              <p className="text-gray-400 text-sm">Click anywhere to close</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentProfile;
